// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — onboarding screens.
//
// ⚠ IN-PROGRESS TEXT LIVES IN THE DOM, NOT IN THE STORE.
// These screens are rebuilt whenever the STEP changes, never on a keystroke. If
// a field's value were stored on every `input`, each keystroke would re-render
// the screen and take the caret and the focus with it — the same fault the news
// page documents for its search box. So text is read from the field at submit
// time, and live feedback (clearing an error, enabling a button) is applied to
// the one affected node directly.
// ─────────────────────────────────────────────────────────────────────────────

import {
    discoveryFailureText,
    isValidTenantHost,
    methodCountLabel,
    normaliseTenantHost,
    onboardingFailureTitle,
    onboardingRail,
    tenantHostError,
    tenantDisplayName,
    tokenStepLabels,
    tokenStepStates,
} from "home/app/mobile/onboarding";
import { activeTenant, tenantRowSubtitle, type KnownTenant } from "home/app/mobile/tenants";
import { type LoginMethod, type TokenStepState } from "home/app/mobile/types";
import { type MobileState } from "home/app/mobile/store";
import { t } from "home/app/mobile/i18n";

import { bind, el, fill } from "home/ui/mobile/dom";
import { chevronLeft, chevronRight, icon, logo, tick } from "home/ui/mobile/icons";

export interface OnboardingActions {
    /** The raw address the user typed, alias and all. */
    submitTenant(tenantUrl: string): void;
    pickMethod(method: LoginMethod): void;
    signIn(email: string, password: string, trustDevice: boolean): void;
    back(): void;
    retry(): void;
    openApp(): void;
    cancelSso(): void;
    /** Check for approval right now, rather than waiting out the poll interval. */
    checkNow(): void;
    /** Pair with, or return to, an installation this device already knows. */
    useTenant(tenant: KnownTenant): void;
    /** Show the address field over a non-empty installation list. */
    addAnother(): void;
    /** Leave the "add another tenant" form for the app, on the tenant still signed in. */
    backToApp(): void;
    forgetTenant(tenant: KnownTenant): void;
}

/** What the address pill shows. The app's own host, not the tenant's. */
function browserChrome(): HTMLElement {
    return el("div", { class: "pqm-chrome" }, [
        el("div", { class: "pqm-chrome-pill" }, [
            icon("lock", 13, "var(--color-ink-5)"),
            el("div", { class: "pqm-chrome-url", text: window.location.host }),
        ]),
    ]);
}

/** Brand row + the four-segment progress rail. */
function brandRow(state: MobileState): HTMLElement {
    const segments = onboardingRail(state.onboarding).map((filled) =>
        el("div", { class: `pqm-rail-seg${filled ? " is-filled" : ""}` }),
    );

    // The lockup is ARTWORK. It used to be a rounded square plus the word
    // "PQFORCE" set in the body font — a stand-in for the real logo, which the
    // repo did not carry. It does: `logo()` transcribes the portal's own file.
    // 24px rather than the 27px the app screens use, matching the 22px placeholder
    // it replaces, so this row stays subordinate to the step heading below it.
    return el("div", { class: "pqm-brand" }, [
        logo(24),
        el(
            "div",
            { class: "pqm-rail", role: "progressbar", "aria-label": t("mobileSignInProgress", "Setup progress") },
            segments,
        ),
    ]);
}

/** Step 1 — which installation does this person work in? */
function stepTenant(state: MobileState, actions: OnboardingActions): HTMLElement {
    const input = el("input", {
        class: "input",
        style: "min-height:48px;font-size:15px",
        type: "url",
        inputmode: "url",
        autocapitalize: "none",
        autocorrect: "off",
        spellcheck: "false",
        id: "pqm-tenant",
        placeholder: "demo.pqforce.com",
        value: state.tenantInput,
    });

    const errorText = el("div", { class: "pqm-field-error-text" });
    const error = el("div", { class: "pqm-field-error", hidden: true, role: "alert" }, [
        icon("warningCircle", 14, "var(--color-accent-650)"),
        errorText,
    ]);

    const showError = (message: string) => {
        errorText.textContent = message;
        error.hidden = false;
        input.setAttribute("aria-invalid", "true");
    };
    // "Error clears on the next keystroke."
    bind(input, "input", () => {
        error.hidden = true;
        input.removeAttribute("aria-invalid");
    });

    if (state.tenantError) showError(state.tenantError);

    const submit = () => {
        const raw = input.value.trim();
        if (!isValidTenantHost(normaliseTenantHost(raw))) {
            showError(tenantHostError());
            input.focus();
            return;
        }
        actions.submitTenant(raw);
    };

    const button = el("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:50px;font-size:15px",
        type: "button",
        text: t("mobileSignInContinue", "Continue"),
    });
    bind(button, "click", submit);
    bind(input, "keydown", (event) => {
        if ((event as KeyboardEvent).key === "Enter") submit();
    });

    // Only on the road in from the app: there is a signed-in tenant to go back to. A first run or a
    // sign-out has nowhere behind this screen.
    const back: HTMLElement[] = [];
    if (state.pairingFromApp) {
        const button = el("button", { class: "btn btn-ghost pqm-step-back", type: "button" }, [
            chevronLeft(),
            document.createTextNode(t("mobileViewBack", "Back")),
        ]);
        bind(button, "click", actions.backToApp);
        back.push(button);
    }

    return el("div", { class: "pqm-step" }, [
        ...back,
        el("div", { class: "pqm-step-head" }, [
            el("div", {
                class: "pqm-h pqm-step-title",
                text: t("mobileSignInWhereTitle", "Where does your team work?"),
            }),
            el("div", {
                class: "pqm-step-body",
                text: t(
                    "mobileSignInWhereBody",
                    "Enter the address of your PQFORCE tenant. Your administrator can give it to you — it looks like the one you use in the browser.",
                ),
            }),
        ]),
        el("div", { class: "field" }, [
            el("label", { for: "pqm-tenant", text: t("mobileSignInAddressLabel", "PQFORCE address") }),
            input,
            error,
        ]),
        button,
        // The one promise this screen makes about where the data goes.
        el("div", {
            class: "pqm-reassure",
            text: t("mobileSignInReassure", "Nothing is sent to us — the app talks only to the address you enter."),
        }),
    ]);
}

/**
 * The returning and recovery screen.
 *
 * ⚠ AN EXPIRED SESSION IS NOT A FIRST RUN, and must not look like one. The
 * person has paired before, we know their installation and which method they
 * used, and sending them back to an empty address field asks them to re-supply
 * something we already have. So it opens with a "Welcome back" card offering the
 * method they used last time, over the list of everything this device knows.
 */
function stepReturning(state: MobileState, actions: OnboardingActions): HTMLElement {
    const children: HTMLElement[] = [];
    const current = activeTenant(state.knownTenants);

    if (state.sessionExpired && current) {
        const reauth = el("button", {
            class: "btn btn-primary btn-block",
            style: "min-height:48px",
            type: "button",
            text: current.methodTitle || t("mobileSignInAgain", "Sign in again"),
        });
        bind(reauth, "click", () => actions.useTenant(current));

        children.push(
            el(
                "div",
                {
                    class: "pqm-recover",
                    role: "status",
                },
                [
                    el("div", { class: "pqm-recover-title", text: t("mobileSignInSessionExpired", "Session expired") }),
                    el("div", {
                        class: "pqm-recover-body",
                        // Names what is at stake. Unsent local work must survive a
                        // re-auth, so saying so is a promise the app has to keep.
                        text: t(
                            "mobileSignInSessionExpiredBody",
                            "Your access to {installation} has ended. Sign in again to pick up where you left off.",
                            { installation: current.displayName || current.host },
                        ),
                    }),
                    reauth,
                ],
            ),
        );
    }

    const rows = state.knownTenants.map((tenant) => {
        const isActive = current !== null && tenant.host === current.host;
        const row = el("button", { class: "pqm-method", type: "button" }, [
            // A 3px bar rather than a dot: accent for the active installation,
            // neutral otherwise. Red as a bar is allowed; as a fill it is not.
            el("div", {
                class: "pqm-prio",
                style: `background:${isActive ? "var(--color-accent)" : "var(--color-neutral-400)"};align-self:stretch`,
            }),
            el("div", { class: "pqm-method-text" }, [
                el("div", { class: "pqm-method-title", text: tenant.host }),
                el("div", { class: "pqm-method-sub", text: tenantRowSubtitle(tenant) }),
            ]),
            isActive && !state.sessionExpired
                ? el("span", { class: "tag tag-accent", text: t("mobileSignInActive", "Active") })
                : chevronRight("var(--color-row-chevron)"),
        ]);
        bind(row, "click", () => actions.useTenant(tenant));
        return row;
    });

    const add = el("button", {
        class: "btn btn-secondary btn-block",
        type: "button",
        text: t("mobileSignInAddInstallation", "Add another tenant"),
    });
    bind(add, "click", actions.addAnother);

    children.push(
        el("div", { class: "pqm-section" }, [
            el("div", { class: "pqm-kicker", text: t("mobileSignInYourInstallations", "Your tenants") }),
            ...rows,
        ]),
        add,
    );

    return el("div", { class: "pqm-step" }, children);
}

/** A transient waiting state: discovery, and the SSO handoff. */
function stepWaiting(title: string, body: string, onCancel?: () => void): HTMLElement {
    const children: HTMLElement[] = [
        el("div", { class: "pqm-waiting-head" }, [
            el("div", { class: "pqm-pulse" }),
            el("div", { class: "pqm-h pqm-waiting-title", text: title }),
        ]),
        el("div", { class: "pqm-waiting-body", text: body }),
    ];

    if (onCancel) {
        const cancel = el("button", {
            class: "btn btn-ghost",
            style: "font-size:12.5px",
            type: "button",
            text: t("mobileSignInCancel", "Cancel"),
        });
        bind(cancel, "click", onCancel);
        children.push(cancel);
    }

    return el("div", { class: "pqm-waiting", role: "status", "aria-live": "polite" }, children);
}

/**
 * Step 2 — the sign-in methods.
 *
 * ⚠ Built from the discovery response. One row per offered method; nothing here
 * assumes there are two, or that one of them is SSO.
 */
function stepMethods(state: MobileState, actions: OnboardingActions): HTMLElement {
    const discovery = state.discovery;
    const host = discovery?.host ?? normaliseTenantHost(state.tenantInput);
    const methods = discovery?.methods ?? [];

    const rows = methods.map((method) => {
        const preferred = method.preferred === true;
        const button = el(
            "button",
            { class: `pqm-method${preferred ? " pqm-method-preferred" : ""}`, type: "button" },
            [
                icon(
                    method.kind === "sso" ? "lockKey" : "envelope",
                    19,
                    preferred ? "var(--color-accent-600)" : "var(--color-ink-2)",
                ),
                el("div", { class: "pqm-method-text" }, [
                    el("div", { class: "pqm-method-title", text: method.title }),
                    el("div", { class: "pqm-method-sub", text: method.subtitle }),
                ]),
                chevronRight(preferred ? "var(--color-accent-600)" : "var(--color-row-chevron)"),
            ],
        );
        bind(button, "click", () => actions.pickMethod(method));
        return button;
    });

    const different = el("button", {
        class: "btn btn-ghost",
        style: "align-self:flex-start;font-size:12.5px",
        type: "button",
        text: t("mobileSignInDifferentAddress", "Use a different address"),
    });
    bind(different, "click", actions.back);

    return el("div", { class: "pqm-step", style: "gap:18px" }, [
        el("div", { class: "pqm-step-head", style: "gap:7px" }, [
            el("div", { class: "pqm-kicker pqm-kicker-accent", text: host }),
            el("div", {
                class: "pqm-h pqm-step-title",
                text: discovery?.displayName || tenantDisplayName(host),
            }),
            el("div", { class: "pqm-step-body", text: methodCountLabel(methods.length) }),
        ]),
        el("div", { class: "pqm-section" }, rows),
        different,
    ]);
}

/** Step 3 — interactive sign-in. */
function stepLogin(state: MobileState, actions: OnboardingActions): HTMLElement {
    const host = state.discovery?.host ?? normaliseTenantHost(state.tenantInput);

    const email = el("input", {
        class: "input",
        style: "min-height:46px;font-size:15px",
        type: "email",
        inputmode: "email",
        autocapitalize: "none",
        autocomplete: "username",
        spellcheck: "false",
        id: "pqm-email",
        placeholder: "nadia.reber@company.ch",
    });
    const password = el("input", {
        class: "input",
        style: "min-height:46px;font-size:15px",
        type: "password",
        autocomplete: "current-password",
        id: "pqm-password",
        placeholder: "••••••••••",
    });
    // Default on: it is what drives refresh-token persistence.
    const trust = el("input", { type: "checkbox", checked: true });

    const error = el("div", { class: "pqm-field-error", hidden: true, role: "alert" }, [
        icon("warningCircle", 14, "var(--color-accent-650)"),
        el("div", { class: "pqm-field-error-text", text: state.onboardingError }),
    ]);
    if (state.onboardingError) error.hidden = false;

    const submit = () => actions.signIn(email.value, password.value, trust.checked);

    const signIn = el("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:50px;font-size:15px",
        type: "button",
        text: t("mobileSignInSubmit", "Sign in"),
    });
    bind(signIn, "click", submit);
    bind(password, "keydown", (event) => {
        if ((event as KeyboardEvent).key === "Enter") submit();
    });

    const back = el("button", {
        class: "btn btn-ghost",
        style: "font-size:12.5px",
        type: "button",
        text: t("mobileSignInBack", "Back"),
    });
    bind(back, "click", actions.back);

    // Not designed yet, and not invented here — it goes to the tenant, which owns
    // the credential. Left as a link-shaped control the shell can point somewhere.
    const forgot = el("button", {
        class: "btn btn-ghost",
        style: "font-size:12.5px",
        type: "button",
        text: t("mobileSignInForgotPassword", "Forgot password"),
    });

    return el("div", { class: "pqm-step pqm-step-tight" }, [
        el("div", { class: "pqm-step-head", style: "gap:6px" }, [
            el("div", { class: "pqm-kicker pqm-kicker-accent", text: host }),
            el("div", { class: "pqm-h pqm-step-title-sm", text: t("mobileSignInTitle", "Sign in") }),
        ]),
        el("div", { class: "field" }, [
            el("label", { for: "pqm-email", text: t("mobileSignInEmail", "Email") }),
            email,
        ]),
        el("div", { class: "field" }, [
            el("label", { for: "pqm-password", text: t("mobileSignInPassword", "Password") }),
            password,
        ]),
        error,
        el("label", { class: "radio", style: "gap:10px;font-size:13px" }, [
            trust,
            el("span", { class: "dot" }),
            document.createTextNode(t("mobileSignInKeepSignedIn", "Keep me signed in on this device")),
        ]),
        signIn,
        el("div", { class: "pqm-actions-row" }, [back, forgot]),
    ]);
}

/**
 * Step 4 — token setup.
 *
 * The three milestones are real requests, so a failure is shown ON THE STEP THAT
 * FAILED rather than as one generic error. That is the difference between "try
 * again" and "your administrator has not switched the mobile endpoint on".
 */
function stepToken(state: MobileState, actions: OnboardingActions): HTMLElement {
    const host = state.discovery?.host ?? normaliseTenantHost(state.tenantInput);
    const labels = tokenStepLabels(host);
    const states = tokenStepStates(state.tokenStep, state.tokenFailedAt);

    const rows = labels.map((label, index) => {
        const status: TokenStepState = states[index]!;
        const mark = el("div", { class: "pqm-check-mark" }, status === "done" ? [tick()] : []);
        if (status === "failed") mark.appendChild(icon("warningCircle", 11, "currentColor"));
        return el("div", { class: `pqm-check is-${status}` }, [
            mark,
            el("div", { class: "pqm-check-label", text: label }),
        ]);
    });

    const children: HTMLElement[] = [
        el("div", {
            class: "pqm-h",
            style: "font-size:22px;letter-spacing:-.02em",
            text: t("mobileSignInSettingUp", "Setting up your session"),
        }),
        el("div", { class: "pqm-checklist", role: "status", "aria-live": "polite" }, rows),
    ];

    // ⚠ Tell them to COME BACK. Approval happens in another tab, and a backgrounded tab does not
    // run timers — so nothing at all moves here until this tab is on screen again. Without saying
    // so, the wait is indistinguishable from a hang, which is exactly how it was reported on dev.
    if (state.tokenFailedAt === null) {
        children.push(
            el("div", {
                class: "pqm-step-body",
                text: t(
                    "mobileSignInApproveHint",
                    "Approve the request on the page that opened, then come back to this tab.",
                ),
            }),
        );
        const check = el("button", {
            class: "btn btn-ghost",
            type: "button",
            text: t("mobileSignInCheckNow", "I have approved — check now"),
        });
        bind(check, "click", actions.checkNow);
        children.push(check);
    }

    if (state.tokenFailedAt !== null) {
        children.push(el("div", { class: "pqm-step-body", text: state.onboardingError }));
        const retry = el("button", {
            class: "btn btn-primary btn-block",
            type: "button",
            text: t("mobileSignInTryAgain", "Try again"),
        });
        bind(retry, "click", actions.retry);
        children.push(retry);
    }

    return el("div", { class: "pqm-step", style: "gap:18px" }, children);
}

/** The approval hand-off: the user is away in the browser approving the token. */
function stepAwaitingApproval(state: MobileState, actions: OnboardingActions): HTMLElement {
    // ⚠ Do not describe a tab that may not exist. window.open() is called from a promise callback,
    // which Safari on iOS blocks — it returns null rather than throwing, so the app carries on
    // waiting for an approval the user was never given the chance to make. Saying "confirm in the
    // tab that just opened" and offering to open it "again" left an iPhone with no way forward and
    // nothing on screen suggesting one, while the same build worked on a desktop browser.
    const blocked = state.approvalOpened === false;
    const wrapper = stepWaiting(
        blocked
            ? t("mobileSignInOneMoreStep", "One more step")
            : t("mobileSignInWaitingApproval", "Waiting for your approval"),
        blocked
            ? t(
                  "mobileSignInPopupBlocked",
                  "Your browser would not open the approval page on its own. Tap the button below to open it, approve the request there, then come back to this tab.",
              )
            : t(
                  "mobileSignInConfirmInTab",
                  "Confirm the access request in the tab that just opened. You will come back here automatically. Do not close this tab.",
              ),
        actions.cancelSso,
    );

    // The tenant bounds how long the request stays approvable; saying so beats a
    // spinner that could mean anything.
    if (state.authorizationDeadline) {
        const when = new Date(state.authorizationDeadline);
        if (!Number.isNaN(when.getTime())) {
            const pad = (n: number) => String(n).padStart(2, "0");
            wrapper.appendChild(
                el("div", {
                    class: "pqm-hint",
                    text: t("mobileSignInExpiresAt", "The request expires at {time}.", {
                        time: `${pad(when.getHours())}:${pad(when.getMinutes())}`,
                    }),
                }),
            );
        }
    }

    if (state.authorizationUrl) {
        // A real anchor tapped by a person is a user gesture, so it opens where window.open() could
        // not — which is why this is the primary action, not a footnote, when the popup was blocked.
        const reopen = el("a", {
            class: blocked ? "btn btn-primary btn-block" : "btn btn-secondary",
            href: state.authorizationUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            text: blocked
                ? t("mobileSignInOpenApproval", "Open the approval page")
                : t("mobileSignInOpenApprovalAgain", "Open the approval page again"),
        });
        wrapper.appendChild(reopen);
    }

    return wrapper;
}

/** Discovery failed. Named copy from the handoff, plus the two offered actions. */
function stepFailed(state: MobileState, actions: OnboardingActions): HTMLElement {
    const host = normaliseTenantHost(state.tenantInput);
    const retry = el("button", {
        class: "btn btn-primary btn-block",
        type: "button",
        text: t("mobileSignInTryAgain", "Try again"),
    });
    bind(retry, "click", actions.retry);
    const different = el("button", {
        class: "btn btn-ghost",
        style: "align-self:flex-start;font-size:12.5px",
        type: "button",
        text: t("mobileSignInDifferentAddress", "Use a different address"),
    });
    bind(different, "click", actions.back);

    return el("div", { class: "pqm-step" }, [
        el("div", { class: "pqm-step-head" }, [
            el("div", { class: "pqm-h pqm-step-title-sm", text: onboardingFailureTitle(state.onboardingErrorStatus) }),
            el("div", { class: "pqm-step-body", text: state.onboardingError || discoveryFailureText(host) }),
        ]),
        retry,
        different,
    ]);
}

/** The end of onboarding — and the first moment an install prompt is appropriate. */
function stepDone(state: MobileState, actions: OnboardingActions): HTMLElement {
    const host = state.profile?.tenantHost ?? normaliseTenantHost(state.tenantInput);
    const firstName = (state.profile?.name ?? "").split(/\s+/)[0] ?? "";

    const open = el("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:50px;font-size:15px",
        type: "button",
        text: t("mobileSignInOpenApp", "Open PQFORCE"),
    });
    bind(open, "click", actions.openApp);

    return el("div", { class: "pqm-step" }, [
        el("div", { class: "pqm-success-mark" }, [icon("check", 22, "currentColor")]),
        el("div", { class: "pqm-step-head" }, [
            el("div", {
                class: "pqm-h pqm-step-title-sm",
                text: firstName
                    ? t("mobileSignInDoneNamed", "You're in, {name}", { name: firstName })
                    : t("mobileSignInDone", "You're in"),
            }),
            el("div", {
                class: "pqm-step-body",
                text: t(
                    "mobileSignInDoneBody",
                    "Signed in to {host}. The address is remembered, so next time you go straight to your TODOs.",
                    { host },
                ),
            }),
        ]),
        open,
    ]);
}

/** Render the onboarding flow into `root`. Called only when the step changes. */
export function renderOnboarding(root: HTMLElement, state: MobileState, actions: OnboardingActions): void {
    let body: HTMLElement;

    switch (state.onboarding) {
        case "discovering":
            body = stepWaiting(
                t("mobileSignInChecking", "Checking {host}", { host: normaliseTenantHost(state.tenantInput) }),
                t("mobileSignInCheckingBody", "Asking which sign-in methods this tenant offers."),
            );
            break;
        case "options":
            body = stepMethods(state, actions);
            break;
        case "login":
            body = stepLogin(state, actions);
            break;
        case "sso":
            body = stepAwaitingApproval(state, actions);
            break;
        case "validating":
            body = stepToken(state, actions);
            break;
        case "done":
            body = stepDone(state, actions);
            break;
        case "failed":
            body = stepFailed(state, actions);
            break;
        case "url":
        default:
            // A device that has paired before opens on its installations, not on
            // an empty address field — unless the user asked to add another.
            body =
                state.knownTenants.length > 0 && !state.addingTenant
                    ? stepReturning(state, actions)
                    : stepTenant(state, actions);
            break;
    }

    fill(root, [browserChrome(), el("div", { class: "pqm-scroll pqm-onb-body" }, [brandRow(state), body])]);
}
